import { colorCssVariables, type ColorName } from "@skailer-com/uikit";

function getHSLAColor(cssVariable: string, alpha: number = 1.0): string {
  const color = getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim();

  if (!color) {
    return `hsla(0, 0%, 0%, ${alpha})`;
  }

  if (color.startsWith("#")) {
    const value = color.slice(1);
    const hex =
      value.length === 3 || value.length === 4
        ? value
            .split("")
            .map((digit) => digit + digit)
            .join("")
        : value;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return rgbToHsla(r, g, b, alpha);
  }

  // hsl(220, 10%, 12%) or hsla(220, 10%, 12%, 1) or hsl(220deg 10% 12% / 1)
  const hslMatch = color.match(
    /^hsla?\(\s*([\d.]+)(?:deg)?\s*[,\s]\s*([\d.]+)%\s*[,\s]\s*([\d.]+)%/i
  );
  if (hslMatch) {
    return `hsla(${hslMatch[1]}, ${hslMatch[2]}%, ${hslMatch[3]}%, ${alpha})`;
  }

  // rgb(23, 27, 31) or rgba(23, 27, 31, 1) or rgb(23 27 31)
  const rgbMatch = color.match(/^rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)/i);
  if (rgbMatch) {
    return rgbToHsla(
      parseFloat(rgbMatch[1]),
      parseFloat(rgbMatch[2]),
      parseFloat(rgbMatch[3]),
      alpha
    );
  }

  return resolveColorViaCanvas(color, alpha);
}

function rgbToHsla(r: number, g: number, b: number, alpha: number): string {
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = b / 255;

  const max = Math.max(r1, g1, b1);
  const min = Math.min(r1, g1, b1);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r1:
        h = (g1 - b1) / d + (g1 < b1 ? 6 : 0);
        break;
      case g1:
        h = (b1 - r1) / d + 2;
        break;
      case b1:
        h = (r1 - g1) / d + 4;
        break;
    }
    h /= 6;
  }

  return `hsla(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%, ${alpha})`;
}

function resolveColorViaCanvas(color: string, alpha: number): string {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return `hsla(0, 0%, 0%, ${alpha})`;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return rgbToHsla(r, g, b, alpha);
  } catch {
    return `hsla(0, 0%, 0%, ${alpha})`;
  }
}

function getUIKitColorHSLA(colorName: ColorName, alpha: number = 1.0): string {
  return getHSLAColor(colorCssVariables[colorName], alpha);
}

export { getHSLAColor, getUIKitColorHSLA };
